import { none, useHookstate } from "@hookstate/core"
import InputField from "../../../components/input-field/InputField";
import { useMyCourses } from "../../../hooks/course";
import { Class } from "../../../types";
import TrashRed from "../../../assets/icons/TrashRed";
import DatePicker from "react-datepicker";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { timeSlots } from "../../../misc/constants";
import DefaultButton from "../../../components/default-button/DefaultButton";
import ArrowLeft from "../../../assets/icons/ArrowLeft";
import Tick from "../../../assets/icons/Tick";
import { simpleGetter } from "../../../actions/common";
import { examApi } from "../../../api/api";
import { showSnackbar } from "../../../misc/utils";

type ExamForm = {
    name: string;
    course_id: string;
    class_ids: string[];
    start_date: Date | null;
    start_time: string;
    end_time: string;
}

type ExamFormStringTypes = 'name' | 'course_id' | 'start_time' | 'end_time';

type ExamState = {
    form: ExamForm;
}

type ExamCreateProps = {
    toggleModal: (trigger?: boolean) => () => void;
}

export const ExamCreate = ({ toggleModal }: ExamCreateProps) => {
    const localState = useHookstate<ExamState>({
        form: {
            name: '',
            course_id: '',
            class_ids: [],
            start_date: null,
            start_time: '',
            end_time: ''
        }
    })
    const myCourses = useMyCourses();
    const state = localState.get();
    const form = state.form;

    const coursesOptions = myCourses.map(c => ({ label: c.name, value: c.id }))
    const currentClasses = myCourses.find(c => c.id === parseInt(form.course_id))?.classes
    const classOptions = currentClasses
        ?.filter(cl => !form.class_ids.map(parseInt).includes(cl.id))
        ?.map(cl => ({ label: cl.name, value: cl.id }))

    const selectedClasses = form.class_ids.map(id => currentClasses?.find(cl => cl.id === parseInt(id)) || {} as Class)

    const updateFormString = <K extends ExamFormStringTypes>(key: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        localState.form[key].set(e.target.value)
    }

    const onDateChange = (date: Date | null) => {
        localState.form.start_date.set(date);
    }

    const addClass = (e: React.ChangeEvent<HTMLSelectElement>) => {
        localState.form.class_ids.merge([e.target.value])
    }

    const removeClass = (cl: Class) => () => {
        const foundIndex = form.class_ids.findIndex(id => parseInt(id) === cl.id)
        if (foundIndex >= 0) {
            localState.form.class_ids[foundIndex].set(none)
        }
    }

    const validateForm = () => form.name && form.course_id && form.class_ids.length && form.start_date && timeSlots.indexOf(form.end_time) > timeSlots.indexOf(form.start_time)

    const createExam = async () => {
        const response = await simpleGetter(examApi.create, state.form)
        if (response.success) {
            showSnackbar('Успешно', 'success')
            toggleModal(true)()
        }
    }

    // const updateForm = <K extends keyof ExamForm>(key: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    //     const fieldState = localState.form[key];
    //     const currentValue = fieldState.get();

    //     if ((e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) && key in ['name', 'course_id',]) {
    //         localState.form[key].set(e.target.value)
    //     }
    // }

    return (
        <div className="flex flex-col gap-8">
            <span className="text-2xl leading-[26.4px] text-valley-black">
                Назначение заседания
            </span>
            <InputField
                type="text"
                id="exam-name"
                label="Название заседания"
                handleChange={updateFormString('name')}
                value={form.name}
                additionalClassName="bg-valley-background-blue py-3 px-4 rounded-lg"
            />
            <InputField
                type="select"
                id="exam-course-id"
                label="Курс"
                handleChange={updateFormString('course_id')}
                value={form.course_id}
                options={coursesOptions}
                additionalClassName="bg-valley-background-blue py-3 px-4 rounded-lg"
            />
            <div className="flex flex-col gap-4">
                <label
                    className="block text-sm font-semibold text-valley-gray"
                >
                    Класс (-ы)
                </label>
                {Boolean(selectedClasses.length) && (
                    <div className="flex flex-row gap-2">
                        {selectedClasses.map(cl => (
                            <div className="flex flex-row items-center p-2 rounded-xl gap-2 bg-[#0068FF1F]">
                                <span className="text-[13px] leading-[130%] text-valley-black">
                                    {cl.name}
                                </span>
                                <button onClick={removeClass(cl)}>
                                    <TrashRed additionalStyle="text-valley-light-gray" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <InputField
                    type="select"
                    id="exam-class-id"
                    handleChange={addClass}
                    options={classOptions}
                    additionalClassName="bg-valley-background-blue py-3 px-4 rounded-lg"
                />
            </div>
            <div className="flex flex-row gap-3">
                <div className="flex flex-col flex-1 text-valley-black gap-2">
                    <p className="text-[#5F6B88] font-medium text-sm">Дата</p>
                    <DatePicker
                        selected={form.start_date}
                        onChange={onDateChange}
                        customInput={<CustomDateInput hasTime={false} />}
                        dateFormat="dd/MM/yyyy"
                    />
                </div>
                <div className="flex flex-col flex-1 gap-2">
                    <InputField
                        wrapperClassName="flex flex-col gap-2"
                        additionalClassName="px-3 py-[10.5px] bg-valley-background-blue"
                        type="select"
                        id={"start-time"}
                        label="Начало"
                        value={form.start_time}
                        handleChange={updateFormString("start_time")}
                        options={timeSlots.map(t => ({ label: t, value: t }))}
                    />
                </div>
                <div className="flex flex-col flex-1 gap-2">
                    <InputField
                        wrapperClassName="flex flex-col gap-2"
                        additionalClassName="px-3 py-[10.5px] bg-valley-background-blue"
                        type="select"
                        id={"end-time"}
                        label="Конец"
                        value={form.end_time}
                        handleChange={updateFormString("end_time")}
                        options={timeSlots.map(t => ({ label: t, value: t }))}
                    />
                </div>
            </div>
            <div className="flex flex-row gap-3">
                <DefaultButton
                    buttonText="Вернуться"
                    additionalStyle="flex-1 bg-[#5F6B88] text-white px-8 py-3 text-nowrap"
                    Icon={ArrowLeft}
                    onClick={toggleModal()}
                />
                <DefaultButton
                    buttonText="Создать экзамен"
                    additionalStyle="flex-1 bg-valley-main text-white px-8 py-3 text-nowrap"
                    Icon={Tick}
                    iconStyle="text-white"
                    onClick={createExam}
                    disabled={!validateForm()}
                />
            </div>
        </div>
    )
}